/**
 * @fileoverview Data Retention Service for managing data retention types
 * Facade over DataRetentionSchedule.DataType namespace for React/TS codebase
 */
import { DataRetentionSchedule } from '@/js/external-js/variableModules/namespaces/DataRetentionSchedule.namespace';
import { db } from '@/js/external-js/config/main_config';
import { collection, doc, getDocs, setDoc, deleteDoc, writeBatch, query, where } from 'firebase/firestore';

/** Singleton instance of the DataType namespace */
let DataTypeLib: typeof DataRetentionSchedule.DataType | null = null;

const initializeDataTypeLib = (): typeof DataRetentionSchedule.DataType => {
  if (!DataTypeLib) {
    DataTypeLib = DataRetentionSchedule.DataType;
  }
  return DataTypeLib;
};

export interface DataPool {
  id: string;
  name: string;
  reason?: string;
  dataTypes?: string[];  // IDs of assigned data types
  systems?: string[];    // IDs of assigned systems/assets
  suppliers?: string[];
  labels?: string[];
  status?: 'active' | 'inactive';
}

// --- Data Types ---

/**
 * Get all data retention types
 */
export const getDataTypes = async (): Promise<DataRetentionSchedule.DataTypeEntity[]> => {
  const lib = initializeDataTypeLib();
  const result = await lib.Get();
  if (Array.isArray(result)) return result;
  if (result && typeof result === 'object' && 'entity' in result) {
    return [(result as any).entity];
  }
  return [];
};

/**
 * Get a single data retention type by ID
 */
export const getDataTypeById = async (id: number): Promise<DataRetentionSchedule.DataTypeEntity | undefined> => {
  const lib = initializeDataTypeLib();
  const result = await lib.Get(id);
  if (result && typeof result === 'object' && 'entity' in result) {
    return (result as any).entity;
  } else {
    console.error('Failed to get data type by ID:', id, 'Result:', result);
  }
  return undefined;
};

/**
 * Create a new data retention type
 */
export const createDataType = async (id: number, data: Partial<DataRetentionSchedule.DataTypeEntity>) => {
  const lib = initializeDataTypeLib();
  return lib.Add(id, data);
};

/**
 * Update an existing data retention type
 */
export const updateDataType = async (id: number, updates: Partial<DataRetentionSchedule.DataTypeEntity>) => {
  const lib = initializeDataTypeLib();
  return lib.Edit(id, updates);
};

/**
 * Delete a data retention type
 */
export const deleteDataType = async (id: number) => {
  const lib = initializeDataTypeLib();
  return lib.Delete(id);
};

/**
 * Save multiple data types (used by suggestions modal)
 */
export const saveDataTypes = async (types: Partial<DataRetentionSchedule.DataTypeEntity>[]) => {
  const lib = initializeDataTypeLib();
  const promises = types.map(async (t) => {
      const id = t.dataTypeID || Math.floor(Math.random() * 1000000);
      // Check if exists? The API Add might upsert or fail. Assuming Add for new ones.
      // For this specific feature (onboarding suggestions), we are likely adding new ones.
      return lib.Add(id, t);
  });
  return Promise.all(promises);
};


// --- Data Pools ---

const getPoolsCollection = (companyID: string) => {
    return collection(db, 'customers', companyID, 'dataPools');
};

/**
 * Get all data pools
 */
export const getDataPools = async (companyID: string): Promise<DataPool[]> => {
    if (!companyID) return [];
    const snap = await getDocs(getPoolsCollection(companyID));
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as DataPool));
};

/**
 * Save (Create/Update) a single data pool
 */
export const saveDataPool = async (companyID: string, pool: DataPool) => {
    if (!companyID) throw new Error("Company ID required");
    const ref = doc(db, 'customers', companyID, 'dataPools', pool.id);
    await setDoc(ref, pool, { merge: true });
};

/**
 * Delete a data pool
 */
export const deleteDataPool = async (companyID: string, poolID: string) => {
    if (!companyID) throw new Error("Company ID required");
    const ref = doc(db, 'customers', companyID, 'dataPools', poolID);
    await deleteDoc(ref);
};

/**
 * Save multiple data pools (batch)
 */
export const saveDataPools = async (companyID: string, pools: DataPool[]) => {
    if (!companyID) throw new Error("Company ID required");
    if (pools.length === 0) return;

    const batch = writeBatch(db);
    pools.forEach(pool => {
        const ref = doc(db, 'customers', companyID, 'dataPools', pool.id);
        batch.set(ref, pool, { merge: true });
    });
    await batch.commit();
};

export default {
  getDataTypes,
  getDataTypeById,
  createDataType,
  updateDataType,
  deleteDataType,
  saveDataTypes,
  getDataPools,
  saveDataPool,
  deleteDataPool,
  saveDataPools
};
